import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/auth';
import { forgetCsrf } from '@/api/client';
import { queryKeys } from '@/lib/queryKeys';
import type { CurrentUser, EnrollmentOffer, SignInResult } from '@/types/api';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';

/**
 * The signed-in account.
 *
 * `me` is the single source of truth for whether anyone is signed in: the app
 * renders its shell only while this query holds a user.
 */
export function useCurrentUser() {
  return useQuery({ queryKey: queryKeys.me, queryFn: authApi.me });
}

const NO_ROLES: readonly string[] = [];

/**
 * The signed-in account's roles, for deciding which controls to show (the
 * server decides what is allowed). Reads the session the app already holds:
 * no request of its own, so any component may ask.
 */
export function useRoles(): readonly string[] {
  const me = useQuery({ queryKey: queryKeys.me, queryFn: authApi.me, staleTime: Infinity, select: (user) => user.roles });
  return me.data ?? NO_ROLES;
}

/** What starting and ending a session does to the cache. */
export function useSession() {
  const client = useQueryClient();
  return {
    /**
     * Shows the app at once with who signed in, and reads the whole profile
     * behind it: signing in answers with the login and roles only.
     */
    signedIn(user: SignInResult['user']): void {
      client.setQueryData(queryKeys.me, user as CurrentUser);
      void client.invalidateQueries({ queryKey: queryKeys.me });
    },
    /**
     * The CSRF token is paired with the cookie of the session that just ended,
     * so it goes with it. Resetting every query then drops the data that
     * session could see and asks again who we are; the answer is nobody, which
     * is what brings the sign-in form back.
     */
    signedOut(): void {
      forgetCsrf();
      void client.resetQueries();
    },
  };
}

export function useSignIn(callbacks?: MutationCallbacks<SignInResult, { login: string; password: string; totpCode?: string }>) {
  return useApiMutation(authApi.login, () => [], callbacks);
}

/** Finishes a sign-in that stopped at binding an authenticator. */
export function useCompleteEnrollment(callbacks?: MutationCallbacks<SignInResult, { token: string; code: string }>) {
  return useApiMutation(authApi.completeEnrollment, () => [], callbacks);
}

export function useSignOut(callbacks?: MutationCallbacks<{ ok: boolean }, void>) {
  return useApiMutation(() => authApi.logout(), () => [], callbacks);
}

export function useUpdateProfile(callbacks?: MutationCallbacks<{ displayName: string | null }, string | null>) {
  // The name shows in the team list too.
  return useApiMutation(authApi.updateProfile, () => [queryKeys.me, queryKeys.users.all], callbacks);
}

export function useChangePassword(
  callbacks?: MutationCallbacks<{ revokedSessions: number }, { currentPassword: string; newPassword: string }>,
) {
  return useApiMutation(authApi.changePassword, () => [], callbacks);
}

export function useStartMfaSetup(callbacks?: MutationCallbacks<EnrollmentOffer, void>) {
  return useApiMutation(() => authApi.startMfaSetup(), () => [], callbacks);
}

export function useActivateMfa(callbacks?: MutationCallbacks<{ ok: boolean }, { token: string; code: string }>) {
  return useApiMutation(authApi.activateMfa, () => [queryKeys.me, queryKeys.users.all], callbacks);
}
